/**
 * Fait : l'écran de la BIBLIOTHÈQUE DE CONSIGNES — la liste, la recherche,
 *   l'écriture d'une consigne neuve, et la modification de la sienne. C'est le
 *   lot 6 du chantier `40.o` : cinq routes qui n'avaient aucune porte en ont
 *   une ici.
 *
 *   LA BIBLIOTHÈQUE EST COMMUNE. On voit les consignes de toute la famille ;
 *   on ne réécrit que les siennes. L'écran n'offre « Modifier » et
 *   « Supprimer » que sur celles qu'on a écrites — par politesse, pas par
 *   protection : c'est la route qui refuse (critère 149).
 *
 *   LA RECHERCHE SE DEMANDE, ELLE NE SE DEVINE PAS. Elle part au serveur à la
 *   soumission, pas à chaque frappe : un appel par caractère ferait bavarder
 *   la liaison, et surtout une liste qui se réordonne pendant qu'on tape est
 *   illisible pour qui la parcourt au clavier ou à la voix.
 *
 * Ne fait pas : il ne pagine pas — on paginera quand une liste débordera, le
 *   plan le dit — et n'écoute aucun direct : le canal ne parle que par projet,
 *   et une consigne n'appartient à aucun. C'est la VERSION qui rend les
 *   écritures concurrentes lisibles, et un retour sur l'onglet qui recharge.
 */

import { useCallback, useEffect, useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { Champ } from '../gabarits/Champ.tsx';
import { Partie } from '../gabarits/Partie.tsx';
import {
  ecritUneConsigne,
  listeLesConsignes,
  litLaConsigne,
  modifieLaConsigne,
  supprimeLaConsigne,
  type ConsigneVue,
  type DemandeDeConsigne,
} from './api-consignes.ts';
import { ConsigneDeLaBibliotheque } from './ConsigneDeLaBibliotheque.tsx';
import { FormulaireDeConsigne } from './FormulaireDeConsigne.tsx';

/** Ce que l'écran est en train de faire. */
type Chargement = 'en_cours' | 'pret' | 'injoignable';

/** Le formulaire ouvert, s'il y en a un. */
type Redaction =
  | null
  | { readonly quoi: 'neuve' }
  | { readonly quoi: 'reecriture'; readonly consigne: ConsigneVue };

export function PageConsignes() {
  const [consignes, setConsignes] = useState<ConsigneVue[]>([]);
  const [chargement, setChargement] = useState<Chargement>('en_cours');
  const [recherche, setRecherche] = useState('');
  const [rechercheEnCours, setRechercheEnCours] = useState('');
  const [redaction, setRedaction] = useState<Redaction>(null);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  const charge = useCallback(async (filtre: string) => {
    setChargement('en_cours');
    const issue = await listeLesConsignes(filtre);

    if (issue.bon) {
      setConsignes(issue.valeur.prompts);
      setChargement('pret');
      return;
    }

    setChargement(issue.injoignable === true ? 'injoignable' : 'pret');
    setRefus(issue.erreur.message);
  }, []);

  useEffect(() => {
    void charge(rechercheEnCours);
  }, [charge, rechercheEnCours]);

  /*
   * LE RETOUR SUR L'ONGLET RECHARGE. Une consigne n'a aucun canal de
   * diffusion : deux onglets ouverts ne s'avertissent pas. C'est la
   * compensation que le plan prévoit nommément, avec la version qui rend les
   * conflits lisibles.
   */
  useEffect(() => {
    const auRetour = () => {
      if (document.visibilityState === 'visible') void charge(rechercheEnCours);
    };

    document.addEventListener('visibilitychange', auRetour);

    return () => document.removeEventListener('visibilitychange', auRetour);
  }, [charge, rechercheEnCours]);

  /** Envoie le formulaire ouvert, quel qu'il soit, et rend compte. */
  async function envoie(demande: DemandeDeConsigne): Promise<void> {
    if (redaction === null) return;

    setEnvoiEnCours(true);
    setRefus(null);

    const issue =
      redaction.quoi === 'neuve'
        ? await ecritUneConsigne(demande)
        : await modifieLaConsigne(redaction.consigne.id, {
            ...demande,
            version: redaction.consigne.version,
          });

    setEnvoiEnCours(false);

    if (!issue.bon) {
      setRefus(issue.erreur.message);
      return;
    }

    setMessage(
      redaction.quoi === 'neuve'
        ? `« ${demande.titre} » est rangée dans la bibliothèque.`
        : `« ${demande.titre} » est enregistrée.`,
    );
    setRedaction(null);
    await charge(rechercheEnCours);
  }

  /**
   * Ouvre la réécriture sur l'état FRAIS de la consigne, pas sur celui que la
   * liste montre. Sans cela, on partirait d'une version peut-être dépassée et
   * l'on recevrait un conflit après avoir tout retapé.
   */
  async function ouvreLaReecriture(consigne: ConsigneVue): Promise<void> {
    setRefus(null);
    const issue = await litLaConsigne(consigne.id);

    if (!issue.bon) {
      setRefus(issue.erreur.message);
      await charge(rechercheEnCours);
      return;
    }

    setRedaction({ quoi: 'reecriture', consigne: issue.valeur });
  }

  /** Supprime, puis recharge — la ligne ne part pas avant que le serveur l'ait dit. */
  async function supprime(consigne: ConsigneVue): Promise<void> {
    setRefus(null);
    const issue = await supprimeLaConsigne(consigne.id);

    if (!issue.bon) {
      setRefus(issue.erreur.message);
    } else {
      setMessage(`« ${consigne.titre} » est supprimée.`);
    }

    await charge(rechercheEnCours);
  }

  return (
    <Partie
      etiquette="Consignes"
      titre="La bibliothèque de consignes"
      nature="travail"
      niveauDeTitre={2}
      aide={
        'Les façons de demander que la famille a trouvées bonnes. ' +
        'Tout le monde les lit ; chacun ne modifie que les siennes.'
      }
      data-ecran="consignes"
    >
      {message !== null && <Annonce data-role="message-des-consignes">{message}</Annonce>}

      {refus !== null && (
        /* `nature="refus"` autant qu'`urgence` : l'urgence dit QUAND
           l'annoncer, la nature dit CE QUE C'EST. Sans elle, un refus
           s'affichait en texte noir ordinaire, indiscernable du reste pour qui
           voit mal — le défaut même que l'en-tête du gabarit raconte avoir
           corrigé ailleurs. */
        <Annonce urgence="urgent" nature="refus" data-role="refus-des-consignes">
          {refus}
        </Annonce>
      )}

      <form
        data-role="recherche-de-consignes"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          setRechercheEnCours(recherche);
        }}
      >
        <Champ
          libelle="Chercher une consigne"
          aide="Un mot de son titre, de sa description, ou de son texte."
          type="search"
          name="recherche-de-consignes"
          data-role="mot-cherche"
          value={recherche}
          onChange={(evenement) => setRecherche(evenement.target.value)}
        />

        <div className="barre-de-boutons">
          <button type="submit" data-role="lancer-la-recherche">
            Chercher
          </button>

          {rechercheEnCours !== '' && (
            <button
              type="button"
              data-role="vider-la-recherche"
              onClick={() => {
                setRecherche('');
                setRechercheEnCours('');
              }}
            >
              Voir toute la bibliothèque
            </button>
          )}
        </div>
      </form>

      {redaction === null && (
        <div className="barre-de-boutons">
          <button
            type="button"
            data-role="principal"
            onClick={() => {
              setRedaction({ quoi: 'neuve' });
              setMessage(null);
            }}
          >
            Écrire une consigne
          </button>
        </div>
      )}

      {redaction !== null && (
        <FormulaireDeConsigne
          /*
           * LA CLÉ N'EST PAS DÉCORATIVE — sans elle, passer de la réécriture
           * d'une consigne à celle d'une AUTRE gardait les champs de la
           * première : React réutilisait l'instance, et les valeurs de départ
           * ne sont lues qu'au montage. On enregistrait alors le texte de A
           * sur la consigne B, avec la version de B — donc sans conflit et
           * sans un mot. Trouvé par une épreuve indépendante le 2026-08-25.
           */
          key={redaction.quoi === 'neuve' ? 'neuve' : redaction.consigne.id}
          {...(redaction.quoi === 'reecriture' ? { depart: redaction.consigne } : {})}
          libelleDEnvoi={redaction.quoi === 'neuve' ? 'Ranger cette consigne' : 'Enregistrer'}
          enCours={envoiEnCours}
          surEnvoi={(demande) => void envoie(demande)}
          surAbandon={() => setRedaction(null)}
        />
      )}

      {chargement === 'en_cours' && (
        <p aria-busy="true" data-etat="chargement">
          Ouverture de la bibliothèque…
        </p>
      )}

      {chargement === 'injoignable' && (
        <p data-etat="indisponible">
          La bibliothèque n’a pas répondu. Rien n’a été perdu — réessayez la recherche.
        </p>
      )}

      {chargement === 'pret' && consignes.length === 0 && (
        <p data-etat="vide" data-role="bibliotheque-vide">
          {rechercheEnCours === ''
            ? 'Aucune consigne pour l’instant. La première que vous écrirez sera lisible par toute la famille.'
            : `Aucune consigne ne contient « ${rechercheEnCours} ».`}
        </p>
      )}

      {chargement === 'pret' && consignes.length > 0 && (
        <ul className="empilement" data-liste="consignes">
          {consignes.map((consigne) => (
            <ConsigneDeLaBibliotheque
              key={consigne.id}
              consigne={consigne}
              surModification={() => void ouvreLaReecriture(consigne)}
              surSuppression={() => void supprime(consigne)}
            />
          ))}
        </ul>
      )}
    </Partie>
  );
}
